import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogOut, Menu, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import Logo from './Logo';
import LanguageSelector from './LanguageSelector';

const Header = ({ dark = false }) => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const links = [
    { to: '/exchange', label: t.nav?.exchange || 'Échange', testid: 'nav-exchange' },
    { to: '/trading', label: t.p2p?.title || 'Trading Direct', testid: 'nav-trading', badge: t.p2p?.badge || 'Nouveau' },
    { to: '/send-money', label: t.nav?.sendMoney || "Envoi d'argent", testid: 'nav-send-money' },
    { to: '/cards', label: t.nav?.cards || 'Cartes', testid: 'nav-cards' },
    { to: '/business', label: t.nav?.business || 'Business', testid: 'nav-business' },
  ];
  if (user) links.push({ to: '/dashboard', label: t.common?.dashboard || 'Tableau de bord', testid: 'nav-dashboard' });
  if (user) links.push({ to: '/alerts', label: t.alerts?.navLabel || 'Alertes', testid: 'nav-alerts' });
  if (user?.role === 'admin') links.push({ to: '/admin', label: 'Admin', testid: 'nav-admin' });

  const base = dark
    ? { bg: 'bg-black/70', text: 'text-white/70 hover:text-white hover:bg-white/10', active: 'text-white bg-white/10' }
    : { bg: 'bg-white/80', text: 'text-gray-600 hover:text-gray-900 hover:bg-gray-100', active: 'text-gray-900 bg-gray-100' };

  return (
    <motion.nav
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className={`sticky top-0 z-50 ${base.bg} backdrop-blur-xl`}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between">
        <Logo size="default" dark={dark} />

        <div className="hidden lg:flex items-center space-x-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${location.pathname === l.to ? base.active : base.text}`}
            >
              {l.label}
              {l.badge && <span className="ml-1.5 text-[9px] font-extrabold uppercase gradient-pink-violet text-white px-1.5 py-0.5 rounded-full align-middle">{l.badge}</span>}
            </Link>
          ))}
        </div>

        <div className="flex items-center space-x-3">
          <LanguageSelector dark={dark} />
          {user ? (
            <button
              onClick={logout}
              className={`hidden sm:flex items-center space-x-2 text-sm font-semibold px-4 py-2 rounded-full transition-colors ${base.text}`}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span>{t.common?.logout || 'Déconnexion'}</span>
            </button>
          ) : (
            <>
              <Link to="/auth" className={`hidden sm:block text-sm font-semibold px-4 py-2 rounded-full transition-colors ${base.text}`} data-testid="nav-login">
                {t.nav?.login || 'Se connecter'}
              </Link>
              <Link to="/auth" data-testid="nav-signup">
                <button className={`${dark ? 'btn-pill-white' : 'btn-pill-dark'} px-5 py-2.5 text-sm`}>
                  {t.auth?.register || "S'inscrire"}
                </button>
              </Link>
            </>
          )}
          <button className={`lg:hidden p-2 rounded-full ${base.text}`} onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className={`lg:hidden px-6 pb-4 ${dark ? 'bg-black/90' : 'bg-white/95'}`}
        >
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)}
              className={`block px-4 py-3 rounded-2xl text-sm font-semibold ${base.text}`}>
              {l.label}
            </Link>
          ))}
          {!user && (
            <Link to="/auth" onClick={() => setMobileOpen(false)} className={`block px-4 py-3 rounded-2xl text-sm font-semibold ${base.text}`}>
              {t.nav?.login || 'Se connecter'}
            </Link>
          )}
        </motion.div>
      )}
    </motion.nav>
  );
};

export default Header;
